import { Checkbox } from '@doist/icons/brand';
import { Stack } from '@doist/marketist';
import { useTranslation } from 'next-i18next';
import { FeatureItem } from '../../feature-item/feature-item';
import type { Teams } from 'types/pricing';
enum Unlimited {
  starter = 'unlimitedStarter',
  business = 'unlimitedBusiness',
  default = 'unlimited',
}
type LimitedBenefit = {
  key: string;
  type: 'limit';
  limits: { [P in Teams.PricingPlan]: number | Unlimited | null };
};
type InfoBenefit = {
  key: string;
  type: 'info';
  includedIn: { [P in Teams.PricingPlan]: boolean };
  isGift?: boolean;
};
type Benefit = (LimitedBenefit | InfoBenefit) & {
  comingSoon?: boolean;
};
const BENEFITS: Benefit[] = [{
  key: 'teamWorkspace',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: true
  }
}, {
  key: 'activeProjects',
  type: 'limit',
  limits: {
    beginner: 5,
    pro: 300,
    teams: null
  }
}, {
  key: 'smartQuickAdd',
  type: 'info',
  includedIn: {
    beginner: true,
    pro: false,
    teams: false
  }
}, {
  key: 'simpleTaskReminders',
  type: 'info',
  includedIn: {
    beginner: true,
    pro: false,
    teams: false
  }
}, {
  key: 'listBoardViews',
  type: 'info',
  includedIn: {
    beginner: true,
    pro: false,
    teams: false
  }
}, {
  key: 'calendarLayout',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: true,
    teams: false
  }
}, {
  key: 'taskDuration',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: true,
    teams: false
  }
}, {
  key: 'customTaskReminders',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: true,
    teams: false
  }
}, {
  key: 'teamProjects',
  type: 'limit',
  limits: {
    beginner: null,
    pro: null,
    teams: 500
  }
}, {
  key: 'teamCalendar',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: true
  }
}, {
  key: 'granularTeamActivities',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: true
  }
}, {
  key: 'sharedTemplates',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: true
  }
}, {
  key: 'fileUploads',
  type: 'limit',
  limits: {
    beginner: null,
    pro: null,
    teams: null
  }
}, {
  key: 'filterViews',
  type: 'limit',
  limits: {
    beginner: 3,
    pro: 150,
    teams: null
  }
}, {
  key: 'activityHistory',
  type: 'limit',
  limits: {
    beginner: 1,
    pro: Unlimited.default,
    teams: null
  }
}, {
  key: 'collaboration',
  type: 'limit',
  limits: {
    beginner: null,
    pro: null,
    teams: 1000
  }
}, {
  key: 'freeCollaboration',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: false
  }
}, {
  key: 'projectFolders',
  type: 'limit',
  limits: {
    beginner: null,
    pro: null,
    teams: 1000
  }
}, {
  key: 'adminAndMemberRoles',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: true
  }
}, {
  key: 'integrations',
  type: 'info',
  includedIn: {
    beginner: true,
    pro: false,
    teams: false
  }
}, {
  key: 'aiAssistant',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: true,
    teams: false
  }
}, {
  key: 'centralizedBilling',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: false,
    teams: true
  }
}, {
  key: 'deadlines',
  type: 'info',
  includedIn: {
    beginner: false,
    pro: true,
    teams: false
  }
}];
function Benefit({
  plan,
  benefit
}: {
  plan: Teams.PricingPlan;
  benefit: Benefit;
}) {
  const {
    t
  } = useTranslation('pricing_v2_3');
  const description = t(`plans.items.${benefit.key}.description`);
  let shouldRender = true;
  let name = '';
  if (benefit.type === 'limit') {
    const limit = benefit.limits[plan];
    if (limit === Unlimited.default || limit === Unlimited.business || limit === Unlimited.starter) {
      name = t(`plans.items.${benefit.key}.${limit}`);
    } else if (limit === null) {
      shouldRender = false;
    } else {
      name = t(`plans.items.${benefit.key}.limited`, {
        count: limit
      });
    }
  } else if (benefit.type === 'info') {
    shouldRender = benefit.includedIn[plan];
    if (shouldRender) {
      name = t(`plans.items.${benefit.key}.name`);
    }
  }
  return shouldRender ? <li data-sentry-component="Benefit" data-sentry-source-file="benefit-list.tsx">
            <FeatureItem name={name} description={description} toggleVisible={false} icon={<Checkbox width="18" height="18" />} iconColor={'tertiary'} variant="base" badge={benefit.comingSoon ? t('comingSoon') : undefined} data-sentry-element="FeatureItem" data-sentry-source-file="benefit-list.tsx" />
        </li> : null;
}
type BenefitListProps = {
  plan: Teams.PricingPlan;
};
export function BenefitList({
  plan
}: BenefitListProps) {
  return <Stack tag="ul" gap="12" data-sentry-element="Stack" data-sentry-component="BenefitList" data-sentry-source-file="benefit-list.tsx">
            {BENEFITS.map(benefit => <Benefit key={benefit.key} plan={plan} benefit={benefit} />)}
        </Stack>;
}